/**
 * Comments on a line or a file (collaboration phase 2), next to the editor: the open file's threads (or the whole
 * project's), each with its replies, Reply and Resolve/Reopen; a new comment on the cursor's line or on the file. A third
 * tab, Suggestions, lists the changes to the open file proposed with Suggest a change (collab/Suggestions.jsx), with
 * Accept / Decline for editors: a comment is a note on a line, a suggestion a proposed edit of the file. Type
 * "@" to mention a project member (vfs-service `/access`): they get an in-app notification, and an email when their
 * sharing emails are on. Comments are stored with the project (versioned in git) by vfs-service.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { addComment, getAccess, getComments, resolveComment } from '../../services/CollabService';
import { insertMention, matchMentions, mentionables, mentionedEmails, mentionParts, mentionQuery } from '../../utils/mentionModel';
import { ago } from '../../utils/blameModel';
import { ParticipantMark } from '../presence/Presence';
import { suggestionsLabel } from '../../utils/collab3Model';
import { FileSuggestions, useFileSuggestions } from './Suggestions';
import './Collab.css';

function Body({ body, mentions }) {
  return (
    <span className="collab-comment-body">
      {mentionParts(body, mentions).map((part, index) => (part.mention
        // eslint-disable-next-line react/no-array-index-key
        ? <span className="collab-mention" key={index}>{part.text}</span>
        // eslint-disable-next-line react/no-array-index-key
        : <React.Fragment key={index}>{part.text}</React.Fragment>))}
    </span>
  );
}

Body.propTypes = { body: PropTypes.string, mentions: PropTypes.array };

function Comment({ comment }) {
  return (
    <div className="collab-comment">
      <ParticipantMark initials={comment.by?.initials || '?'} label={comment.by?.name} size={20} slot={comment.by?.slot ?? 0} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="collab-small"><strong>{comment.by?.name || 'Someone'}</strong> <span className="collab-muted">· {ago(comment.at)}</span></div>
        <Body body={comment.body} mentions={comment.mentions} />
      </div>
    </div>
  );
}

Comment.propTypes = { comment: PropTypes.object.isRequired };

/** A text box with the @mention picker. `onSubmit(body, mentionEmails)`. */
export function CommentComposer({ people, placeholder, onSubmit, busy = false, autoFocus = false }) {
  const [text, setText] = useState('');
  const [caret, setCaret] = useState(0);
  const [picked, setPicked] = useState([]);
  const [highlight, setHighlight] = useState(0);
  const area = useRef(null);
  const query = mentionQuery(text, caret);
  const options = query ? matchMentions(people, query.query) : [];

  const pick = (person) => {
    const next = insertMention(text, query.start, caret, person);
    setText(next.text);
    setPicked((current) => (current.some((p) => p.email === person.email) ? current : [...current, person]));
    setCaret(next.caret);
    setHighlight(0);
    requestAnimationFrame(() => {
      if (area.current) {
        area.current.focus();
        area.current.setSelectionRange(next.caret, next.caret);
      }
    });
  };

  const submit = async () => {
    const body = text.trim();
    if (!body || busy) { return; }
    const ok = await onSubmit(body, mentionedEmails(body, picked));
    if (ok !== false) {
      setText('');
      setPicked([]);
    }
  };

  return (
    <div className="collab-compose">
      {options.length > 0 && (
        <ul className="collab-mention-menu" data-testid="mention-menu" role="listbox">
          {options.map((person, index) => (
            <li key={person.email} role="presentation">
              <button
                aria-selected={index === highlight}
                className={index === highlight ? 'is-on' : ''}
                role="option"
                onMouseDown={(event) => { event.preventDefault(); pick(person); }}
                type="button"
              >
                <strong>{person.name}</strong> <span className="collab-muted collab-small">{person.email}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <textarea
        aria-label={placeholder}
        autoFocus={autoFocus}
        disabled={busy}
        onChange={(event) => { setText(event.target.value); setCaret(event.target.selectionStart); }}
        onKeyDown={(event) => {
          if (options.length > 0 && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
            event.preventDefault();
            setHighlight((h) => (h + (event.key === 'ArrowDown' ? 1 : options.length - 1)) % options.length);
            return;
          }
          if (options.length > 0 && (event.key === 'Enter' || event.key === 'Tab')) {
            event.preventDefault();
            pick(options[Math.min(highlight, options.length - 1)]);
            return;
          }
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            submit();
          }
        }}
        onSelect={(event) => setCaret(event.target.selectionStart)}
        placeholder={placeholder}
        ref={area}
        value={text}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="collab-btn is-primary" disabled={busy || !text.trim()} onClick={submit} type="button">{busy ? 'Sending…' : 'Comment'}</button>
      </div>
    </div>
  );
}

CommentComposer.propTypes = {
  autoFocus: PropTypes.bool,
  busy: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  people: PropTypes.array.isRequired,
  placeholder: PropTypes.string,
};

function Thread({ thread, active, people, onReply, onResolve, onGoToLine, showPath }) {
  const [replying, setReplying] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (active && ref.current?.scrollIntoView) { ref.current.scrollIntoView({ block: 'nearest' }); }
  }, [active]);
  return (
    <div className={`collab-thread${thread.resolved ? ' is-resolved' : ''}${active ? ' is-active' : ''}`} data-testid="comment-thread" ref={ref}>
      <div className="collab-small collab-muted" style={{ display: 'flex', gap: 6, justifyContent: 'space-between' }}>
        <button className="collab-link collab-small" onClick={() => onGoToLine(thread)} type="button">
          {showPath ? `${thread.path.split('/').pop()}${Number.isInteger(thread.line) ? `:${thread.line}` : ''}` : (Number.isInteger(thread.line) ? `Line ${thread.line}` : 'The file')}
        </button>
        {thread.resolved && <span>Resolved{thread.resolvedBy ? ` by ${String(thread.resolvedBy.name || '').split(/\s+/)[0]}` : ''}</span>}
      </div>
      {thread.lineText && <div className="collab-mono collab-muted collab-small" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{thread.lineText}</div>}
      <Comment comment={thread} />
      {(thread.replies || []).map((reply) => <Comment comment={reply} key={reply.id} />)}
      <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
        {!thread.resolved && <button className="collab-link collab-small" onClick={() => setReplying(!replying)} type="button">Reply</button>}
        <button
          className="collab-link collab-small"
          disabled={busy}
          onClick={async () => { setBusy(true); await onResolve(thread, !thread.resolved); setBusy(false); }}
          type="button"
        >
          {thread.resolved ? 'Reopen' : 'Resolve'}
        </button>
      </div>
      {replying && (
        <CommentComposer
          autoFocus
          busy={busy}
          onSubmit={async (body, mentions) => {
            setBusy(true);
            const ok = await onReply(thread, body, mentions);
            setBusy(false);
            if (ok !== false) { setReplying(false); }
            return ok;
          }}
          people={people}
          placeholder="Reply… (@ to mention)"
        />
      )}
    </div>
  );
}

Thread.propTypes = {
  active: PropTypes.bool,
  onGoToLine: PropTypes.func.isRequired,
  onReply: PropTypes.func.isRequired,
  onResolve: PropTypes.func.isRequired,
  people: PropTypes.array.isRequired,
  showPath: PropTypes.bool,
  thread: PropTypes.object.isRequired,
};

export default function CommentsPanel({
  projectId, path, line = null, threads, activeThreadId = null, you = null, onChanged, onClose, onGoToLine, floating = true, sheet = false, projectOnly = false,
  scope: scopeProp = null, onScopeChange = null, focusSuggestionId = null, suggestionsReloadKey = 0, onSuggest = null, onGoToSuggestionLine = null,
}) {
  // `projectOnly` (Team ▾ › Notes › Comments, no file open): the whole project's threads, without the This file / Project switch.
  // The tab ('file' | 'project' | 'suggestions') is the editor's when it passes `scope` (Open in Code picks Suggestions).
  const [ownScope, setOwnScope] = useState(projectOnly ? 'project' : 'file');
  const withSuggestions = Boolean(path) && !projectOnly;
  const scope = projectOnly ? 'project' : (scopeProp && (scopeProp !== 'suggestions' || withSuggestions) ? scopeProp : ownScope);
  const setScope = (next) => { setOwnScope(next); if (onScopeChange) { onScopeChange(next); } };
  const fileSuggestions = useFileSuggestions(withSuggestions ? projectId : null, withSuggestions ? path : null, suggestionsReloadKey);
  const suggestionsWaiting = (fileSuggestions.answer?.suggestions || []).filter((s) => s.status === 'pending').length;
  const [projectThreads, setProjectThreads] = useState([]);
  const [access, setAccess] = useState(null);
  const [onLine, setOnLine] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [showResolved, setShowResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getAccess(projectId).then((answer) => { if (!cancelled) { setAccess(answer); } }).catch(() => {});
    return () => { cancelled = true; };
  }, [projectId]);

  const loadProject = async () => {
    try {
      const answer = await getComments(projectId);
      setProjectThreads(answer?.threads || []);
    } catch {
      setProjectThreads([]);
    }
  };
  useEffect(() => {
    if (scope === 'project') { loadProject(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, projectId]);

  const people = useMemo(() => mentionables(access, you), [access, you]);
  const shown = (scope === 'file' ? threads : projectThreads)
    .filter((t) => showResolved || !t.resolved || t.id === activeThreadId)
    .sort((a, b) => (a.path === b.path ? (a.line ?? 0) - (b.line ?? 0) : a.path.localeCompare(b.path)));
  const resolvedCount = (scope === 'file' ? threads : projectThreads).filter((t) => t.resolved).length;

  const after = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await onChanged();
      if (scope === 'project') { await loadProject(); }
      return true;
    } catch (e) {
      setError(e.message || 'Not saved');
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <aside className={`collab-comments${floating ? ' is-floating' : ''}${sheet ? ' is-sheet' : ''}`} data-testid="comments-panel">
      <div className="collab-comments-head">
        {projectOnly ? <strong className="collab-small">Every comment in the project</strong> : (
          <div className="collab-seg">
            <button aria-pressed={scope === 'file'} className={scope === 'file' ? 'is-on' : ''} onClick={() => setScope('file')} title="Comments on this file's lines" type="button">This file</button>
            <button aria-pressed={scope === 'project'} className={scope === 'project' ? 'is-on' : ''} onClick={() => setScope('project')} title="Comments on every file of the project" type="button">Project</button>
            {withSuggestions && (
              <button aria-pressed={scope === 'suggestions'} className={scope === 'suggestions' ? 'is-on' : ''} data-testid="comments-tab-suggestions" onClick={() => setScope('suggestions')} title="Changes to this file proposed with Suggest a change" type="button">{suggestionsLabel(suggestionsWaiting)}</button>
            )}
          </div>
        )}
        {!sheet && <button aria-label="Close comments" className="collab-link" onClick={onClose} type="button">✕</button>}
      </div>
      {scope === 'suggestions' ? (
        <div className="collab-comments-list">
          <FileSuggestions
            answer={fileSuggestions.answer}
            focusId={focusSuggestionId}
            onChanged={fileSuggestions.reload}
            onGoToLine={onGoToSuggestionLine}
            onSuggest={onSuggest}
            path={path}
            projectId={projectId}
          />
        </div>
      ) : (
      <div className="collab-comments-list">
        {shown.length === 0 && <div className="collab-muted collab-small">No open comments{scope === 'file' ? ' on this file' : ''}.</div>}
        {shown.map((thread) => (
          <Thread
            active={thread.id === activeThreadId}
            key={thread.id}
            onGoToLine={onGoToLine}
            onReply={(t, body, mentions) => after(() => addComment(projectId, { path: t.path, body, mentions, parentId: t.id }))}
            onResolve={(t, resolved) => after(() => resolveComment(projectId, t.id, resolved))}
            people={people}
            showPath={scope === 'project'}
            thread={thread}
          />
        ))}
        {resolvedCount > 0 && (
          <button className="collab-link collab-small" onClick={() => setShowResolved(!showResolved)} type="button">
            {showResolved ? 'Hide resolved' : `Show ${resolvedCount} resolved`}
          </button>
        )}
      </div>
      )}
      {error && <div className="collab-error collab-small collab-pad">{error}</div>}
      {path && scope !== 'suggestions' && (
        <div>
          <label className="collab-radio collab-small" style={{ padding: '6px 12px 0' }}>
            <input checked={onLine && Number.isInteger(line)} disabled={!Number.isInteger(line)} onChange={(e) => setOnLine(e.target.checked)} type="checkbox" />
            {Number.isInteger(line) ? `On line ${line}` : 'On the file'} of {path.split('/').pop()}
          </label>
          <CommentComposer
            busy={busy}
            onSubmit={(body, mentions) => after(() => addComment(projectId, { path, line: onLine && Number.isInteger(line) ? line : undefined, body, mentions }))}
            people={people}
            placeholder="Add a comment… (@ to mention, ⌘↵ to send)"
          />
        </div>
      )}
    </aside>
  );
}

CommentsPanel.propTypes = {
  activeThreadId: PropTypes.string,
  floating: PropTypes.bool,
  focusSuggestionId: PropTypes.string,
  line: PropTypes.number,
  onChanged: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onGoToLine: PropTypes.func.isRequired,
  onGoToSuggestionLine: PropTypes.func,
  onScopeChange: PropTypes.func,
  onSuggest: PropTypes.func,
  path: PropTypes.string,
  projectId: PropTypes.string.isRequired,
  projectOnly: PropTypes.bool,
  scope: PropTypes.oneOf(['file', 'project', 'suggestions']),
  sheet: PropTypes.bool,
  suggestionsReloadKey: PropTypes.number,
  threads: PropTypes.array.isRequired,
  you: PropTypes.object,
};
